import { useCallback, useEffect, useState } from 'react';

/**
 * Browser fullscreen for one element, kept in step with the document.
 *
 * The listener matters as much as the toggle: fullscreen can also be left with Escape
 * or the browser's own chrome, and without it the control would keep offering to
 * enter a mode the page is already out of.
 */
export function useFullscreen(getElement: () => HTMLElement | null) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const sync = () => setIsFullscreen(Boolean(document.fullscreenElement));

    document.addEventListener('fullscreenchange', sync);

    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggle = useCallback(() => {
    if (document.fullscreenElement) {
      // Both calls reject when the browser refuses — a policy or a missing user
      // gesture. There is nothing to recover, and an unhandled rejection is noise.
      void document.exitFullscreen().catch(() => {});
      return;
    }

    void getElement()
      ?.requestFullscreen()
      .catch(() => {});
  }, [getElement]);

  return { isFullscreen, toggle };
}
